import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageWrapper } from '../components/layout/PageWrapper';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { Seo } from '../components/Seo';

const RULES = [
  { num: 1, icon: '🃏', titleKey: 'rules.r1.title', bodyKey: 'rules.r1.body' },
  { num: 2, icon: '🌙', titleKey: 'rules.r2.title', bodyKey: 'rules.r2.body' },
  { num: 3, icon: '🔫', titleKey: 'rules.r3.title', bodyKey: 'rules.r3.body' },
  { num: 4, icon: '🎙️', titleKey: 'rules.r4.title', bodyKey: 'rules.r4.body' },
  { num: 5, icon: '🗳️', titleKey: 'rules.r5.title', bodyKey: 'rules.r5.body' },
  { num: 6, icon: '🎯', titleKey: 'rules.r6.title', bodyKey: 'rules.r6.body' },
  { num: 7, icon: '⚖️', titleKey: 'rules.r7.title', bodyKey: 'rules.r7.body' },
  { num: 8, icon: '💀', titleKey: 'rules.r8.title', bodyKey: 'rules.r8.body' },
];

export function RulesPage() {
  const { t } = useTranslation('rules');

  return (
    <PageWrapper>
      <Seo title="Правила игры" description="Правила спортивной мафии — роли, дневные речи, голосование, ночные проверки." path="/rules" />
      {/* Page header */}
      <div className="text-center mb-12">
        <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">
          ◆ Спортивная Мафия ◆
        </p>
        <h1 className="font-display text-5xl md:text-6xl font-bold text-mafia-paper mb-4">
          {t('title')}
        </h1>
        <p className="text-mafia-paper-dim max-w-lg mx-auto">{t('subtitle')}</p>
        <OrnamentDivider className="mt-8 max-w-xs mx-auto" />
      </div>

      {/* Rule panels */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
        {RULES.map((rule, i) => (
          <motion.div
            key={rule.num}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.45, delay: (i % 4) * 0.07 }}
            whileHover={{ y: -3, transition: { duration: 0.2 } }}
            className="card-vintage group cursor-default"
          >
            {/* Panel number — watermark */}
            <span className="absolute top-2 right-3 font-mono text-5xl font-black text-mafia-gold/8 select-none leading-none">
              {String(rule.num).padStart(2, '0')}
            </span>

            <div className="p-5">
              <div className="text-2xl mb-3">{rule.icon}</div>
              <h3 className="font-display text-base font-semibold text-mafia-paper mb-2 leading-snug">
                {t(rule.titleKey)}
              </h3>
              <p className="text-mafia-paper-dim text-sm leading-relaxed">{t(rule.bodyKey)}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Full rules summary */}
      <div className="card-vintage p-8 md:p-10">
        <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-4">
          ◆ Подробные правила
        </p>
        <h2 className="font-display text-3xl font-bold text-mafia-paper mb-6">
          {t('fullRules.title')}
        </h2>
        <div className="space-y-4 text-mafia-paper-dim leading-relaxed">
          <p>{t('fullRules.p1')}</p>
          <p>{t('fullRules.p2')}</p>
          <p>{t('fullRules.p3')}</p>
        </div>
      </div>

      {/* Official FIIM rules link */}
      <Link
        to="/rules/official"
        className="group flex items-center justify-between card-vintage p-6 hover:bg-mafia-smoke transition-colors"
      >
        <div>
          <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-1">◆ ФИИМ · Редакция 01.02.2026</p>
          <p className="font-display text-xl font-semibold text-mafia-paper group-hover:text-mafia-gold transition-colors">
            Самые подробные правила
          </p>
          <p className="text-mafia-muted text-sm font-mono mt-1">
            Полный официальный регламент — фолы, очки, апелляции
          </p>
        </div>
        <span className="text-mafia-gold text-2xl ml-4 group-hover:translate-x-1 transition-transform">→</span>
      </Link>
    </PageWrapper>
  );
}
